import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card, Row, Col, Statistic, Progress, Tag, Space, Button, Alert, Spin } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons'
import { fetchMetrics, fetchHealth, selectMetrics, selectHealth, selectMonitorLoading, selectMonitorError } from '../../store/monitorSlice'
import MetricsPanel from './MetricsPanel'
import './SystemHealth.css'

const SystemHealth = () => {
  const dispatch = useDispatch()
  const metrics = useSelector(selectMetrics)
  const health = useSelector(selectHealth)
  const loading = useSelector(selectMonitorLoading)
  const error = useSelector(selectMonitorError)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [refreshInterval, setRefreshInterval] = useState(30000) // 30秒

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (!autoRefresh) return

    const timer = setInterval(() => {
      loadData()
    }, refreshInterval)

    return () => clearInterval(timer)
  }, [autoRefresh, refreshInterval])

  const loadData = () => {
    dispatch(fetchMetrics())
    dispatch(fetchHealth())
  }

  const getHealthStatus = (status) => {
    switch (status) {
      case 'healthy':
        return { color: 'success', icon: <CheckCircleOutlined />, text: '健康' }
      case 'degraded':
        return { color: 'warning', icon: <WarningOutlined />, text: '降级' }
      case 'unhealthy':
        return { color: 'error', icon: <CloseCircleOutlined />, text: '异常' }
      default:
        return { color: 'default', icon: <ClockCircleOutlined />, text: '未知' }
    }
  }

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const formatDuration = (seconds) => {
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${days}天 ${hours}小时 ${minutes}分钟`
  }

  if (loading.metrics && !metrics) {
    return (
      <div className="system-health-loading">
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div className="system-health-container">
      <div className="page-header">
        <h1>系统监控</h1>
        <Space>
          <Button
            type={autoRefresh ? 'primary' : 'default'}
            icon={<ReloadOutlined spin={autoRefresh} />}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? '自动刷新' : '手动刷新'}
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadData}
            loading={loading.metrics || loading.health}
          >
            立即刷新
          </Button>
        </Space>
      </div>

      {(error.metrics || error.health) && (
        <Alert
          message="加载失败"
          description={error.metrics || error.health}
          type="error"
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 健康状态概览 */}
      {health && (
        <Card title="健康状态" className="health-overview-card" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="整体状态"
                value={getHealthStatus(health.status).text}
                prefix={getHealthStatus(health.status).icon}
                valueStyle={{ color: getHealthStatus(health.status).color === 'success' ? '#3f8600' : getHealthStatus(health.status).color === 'warning' ? '#faad14' : '#cf1322' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="运行时间"
                value={health.uptime ? formatDuration(health.uptime) : '未知'}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="数据库"
                value={health.database?.status === 'connected' ? '已连接' : '未连接'}
                prefix={health.database?.status === 'connected' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                valueStyle={{ color: health.database?.status === 'connected' ? '#3f8600' : '#cf1322' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="版本"
                value={health.version || 'N/A'}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* 系统性能指标 */}
      {metrics?.system && (
        <Card title="系统性能" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <div className="metric-item">
                <div className="metric-label">CPU 使用率</div>
                <Progress
                  percent={Math.round(metrics.system.cpu_usage * 100)}
                  status={metrics.system.cpu_usage > 0.8 ? 'exception' : 'normal'}
                />
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div className="metric-item">
                <div className="metric-label">内存使用</div>
                <Progress
                  percent={Math.round((metrics.system.memory_used / metrics.system.memory_total) * 100)}
                  status={(metrics.system.memory_used / metrics.system.memory_total) > 0.8 ? 'exception' : 'normal'}
                  format={() => `${formatBytes(metrics.system.memory_used)} / ${formatBytes(metrics.system.memory_total)}`}
                />
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div className="metric-item">
                <div className="metric-label">Goroutine 数量</div>
                <Statistic value={metrics.system.goroutines} />
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* API 性能指标 */}
      {metrics?.api && (
        <Card title="API 性能" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="总请求数"
                value={metrics.api.total_requests}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="成功请求"
                value={metrics.api.success_requests}
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="失败请求"
                value={metrics.api.failed_requests}
                valueStyle={{ color: '#cf1322' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="平均响应时间"
                value={metrics.api.avg_response_time}
                suffix="ms"
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* 数据库指标 */}
      {metrics?.database && (
        <Card title="数据库性能" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="活跃连接"
                value={metrics.database.connections_active}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="空闲连接"
                value={metrics.database.connections_idle}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="总查询数"
                value={metrics.database.total_queries}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="平均查询时间"
                value={metrics.database.avg_query_time}
                suffix="ms"
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* 业务指标 */}
      {metrics?.business && (
        <Card title="业务指标" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="今日数据量"
                value={metrics.business.daily_data_count}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="活跃用户"
                value={metrics.business.active_users}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="活跃 Token"
                value={metrics.business.active_tokens}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="总交易所"
                value={metrics.business.total_exchanges}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* 任务状态 */}
      {metrics?.tasks && metrics.tasks.length > 0 && (
        <Card title="任务状态" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            {metrics.tasks.map((task, index) => (
              <Col xs={24} sm={12} md={8} key={index}>
                <Card size="small" className="task-card">
                  <div className="task-name">{task.name}</div>
                  <div className="task-status">
                    <Tag color={task.status === 'running' ? 'processing' : task.status === 'success' ? 'success' : 'error'}>
                      {task.status === 'running' ? '运行中' : task.status === 'success' ? '成功' : '失败'}
                    </Tag>
                  </div>
                  {task.last_run && (
                    <div className="task-last-run">
                      最后运行: {new Date(task.last_run).toLocaleString()}
                    </div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* 指标图表 */}
      {metrics && <MetricsPanel metrics={metrics} />}
    </div>
  )
}

export default SystemHealth
